import process from 'node:process';
import { apiRequest } from './cli-helpers.js';
import { getArgValue, hasFlag } from './auth.js';

type Revision = {
  id: string;
  version: string;
  revisionNumber: number;
  provenance: { source?: string; summary?: string } | null;
  createdAt: string;
};

type RestoreResult = {
  id: string;
  number: number | null;
  version: string;
  createdAt: string;
};

function usage(): never {
  console.error(
    'Usage: cofactor workflow revisions <list|get|restore> <workflowId> [revisionNumber] [--limit N] [--before N] [--json]'
  );
  process.exit(1);
}

export async function handleWorkflowRevisions(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const [verb, workflowId, revisionNumberArg] = argv.filter((arg) => !arg.startsWith('-'));
  if (!verb || !workflowId) usage();
  const jsonOutput = hasFlag(argv, '--json');

  if (verb === 'list') {
    const limit = getArgValue(argv, '--limit');
    const before = getArgValue(argv, '--before');
    const query = new URLSearchParams();
    if (limit) query.set('limit', limit);
    if (before) query.set('before', before);
    const suffix = query.size > 0 ? `?${query.toString()}` : '';
    const result = await apiRequest<{
      ok: boolean;
      error?: string;
      revisions?: Revision[];
      nextBefore?: number | null;
    }>(apiUrl, token, 'GET', `/workflows/${workflowId}/revisions${suffix}`);
    if (result.error) throw new Error(result.error);
    if (jsonOutput) return void console.log(JSON.stringify(result, null, 2));
    for (const revision of result.revisions ?? []) {
      console.log(
        `#${revision.revisionNumber}  ${revision.createdAt}  ${revision.provenance?.summary ?? revision.provenance?.source ?? 'Saved version'}`
      );
    }
    if (result.nextBefore) console.log(`Next page: --before ${result.nextBefore}`);
    return;
  }

  const revisionNumber = Number(revisionNumberArg);
  if (!Number.isInteger(revisionNumber) || revisionNumber < 1) usage();
  if (verb === 'get') {
    const result = await apiRequest<{ ok: boolean; error?: string; revision?: Revision }>(
      apiUrl,
      token,
      'GET',
      `/workflows/${workflowId}/revisions/${revisionNumber}`
    );
    if (result.error || !result.revision) throw new Error(result.error ?? 'Revision not found');
    console.log(JSON.stringify(jsonOutput ? result : result.revision, null, 2));
    return;
  }

  if (verb === 'restore') {
    const result = await apiRequest<{ ok: boolean; error?: string; revision?: RestoreResult }>(
      apiUrl,
      token,
      'POST',
      `/workflows/${workflowId}/revisions/${revisionNumber}/restore`
    );
    if (result.error || !result.revision)
      throw new Error(result.error ?? 'Revision restore failed');
    console.log(
      jsonOutput
        ? JSON.stringify(result, null, 2)
        : `Restored revision #${revisionNumber} as revision #${result.revision.number}.`
    );
    return;
  }

  usage();
}
